import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ReactFlow, ReactFlowProvider, Background, Controls,
  useNodesState, useEdgesState, addEdge,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Plus, Trash } from "@phosphor-icons/react";
import {
  BusNode, edgeStyle, NODE_STYLE, SidePanel, Toolbar, ToolbarBtn, ToolbarDivider,
} from "./flow/primitives";

/* v3.2 · Cable Ampacity graphical editor. Layout is a raceway
   cross-section: one RACEWAY node (installation, ambient, depth),
   N CONDUCTOR nodes (each represents one current-carrying conductor
   inside the raceway — the count drives NEC 310.15(C)(1) grouping),
   and one LOAD node (size, length, load current). Edges are visual
   only. The backend consumes:

     { awg_or_kcmil, ambient_c, conductors_in_raceway,
       installation, depth_m, length_m, load_amps }

   `conductors_in_raceway` is computed from the number of CONDUCTOR
   nodes, so users can add/remove conductors and immediately see the
   grouping derate refresh on the result card. */

const Raceway   = (p) => <BusNode {...p} {...NODE_STYLE.RACEWAY}
  sub={(d) => `${d.installation} · ${d.ambient_c}°C · ${d.depth_m} m`} />;
const Conductor = (p) => <BusNode {...p} {...NODE_STYLE.CONDUCTOR}
  sub={(d) => `phase ${d.phase || "—"}`} />;
const Load      = (p) => <BusNode {...p} {...NODE_STYLE.LOAD}
  sub={(d) => `${d.awg_or_kcmil} · ${d.load_amps} A · ${d.length_m} m`} />;

const nodeTypes = { RACEWAY: Raceway, CONDUCTOR: Conductor, LOAD: Load };

/* Arrange conductors in a rough grid inside the raceway node. */
const conductorPos = (i) => ({
  x: 260 + (i % 3) * 90,
  y:  70 + Math.floor(i / 3) * 90,
});

const INITIAL_NODES = [
  { id: "RW", type: "RACEWAY", position: { x: 40, y: 200 },
    data: { id: "RW", tag: "Raceway", installation: "raceway",
             ambient_c: 30, depth_m: 0.6 } },
  { id: "C1", type: "CONDUCTOR", position: conductorPos(0),
    data: { id: "C1", tag: "L1", phase: "A" } },
  { id: "C2", type: "CONDUCTOR", position: conductorPos(1),
    data: { id: "C2", tag: "L2", phase: "B" } },
  { id: "C3", type: "CONDUCTOR", position: conductorPos(2),
    data: { id: "C3", tag: "L3", phase: "C" } },
  { id: "LD", type: "LOAD", position: { x: 620, y: 200 },
    data: { id: "LD", tag: "Load", awg_or_kcmil: "4/0",
             length_m: 30, load_amps: 200 } },
];
const INITIAL_EDGES = [
  { id: "e1", source: "RW", target: "C1", ...edgeStyle() },
  { id: "e2", source: "RW", target: "C2", ...edgeStyle() },
  { id: "e3", source: "RW", target: "C3", ...edgeStyle() },
  { id: "e4", source: "C3", target: "LD", ...edgeStyle() },
];

const SIZE_OPTIONS = [
  "14","12","10","8","6","4","3","2","1","1/0","2/0","3/0","4/0",
  "250","300","350","400","500","600","700","750","800","1000",
];
const FIELDS = {
  RACEWAY: [
    { key: "tag",          label: "Tag",          type: "text" },
    { key: "installation", label: "Installation", type: "select",
      options: [{ value: "raceway", label: "raceway / conduit" },
                { value: "buried",  label: "direct buried" },
                { value: "tray",    label: "cable tray" }] },
    { key: "ambient_c",    label: "Ambient (°C)", type: "number" },
    { key: "depth_m",      label: "Depth (m, buried)", type: "number", step: "0.1" },
  ],
  CONDUCTOR: [
    { key: "tag",   label: "Tag",   type: "text" },
    { key: "phase", label: "Phase", type: "select",
      options: ["A", "B", "C", "N", "G"] },
  ],
  LOAD: [
    { key: "tag",          label: "Tag",         type: "text" },
    { key: "awg_or_kcmil", label: "Size (AWG / kcmil)", type: "select",
      options: SIZE_OPTIONS },
    { key: "length_m",     label: "Length (m)",  type: "number" },
    { key: "load_amps",    label: "Load (A)",    type: "number" },
  ],
};

/* Serialise the graph into the /api/studies/cable-ampacity payload. */
export function graphToCableAmpacity(nodes) {
  const rw   = nodes.find((n) => n.type === "RACEWAY")?.data || {};
  const load = nodes.find((n) => n.type === "LOAD")?.data    || {};
  const conductors = nodes.filter((n) => n.type === "CONDUCTOR").length;
  return {
    awg_or_kcmil:          load.awg_or_kcmil || "4/0",
    ambient_c:             Number(rw.ambient_c || 30),
    conductors_in_raceway: Math.max(1, conductors),
    installation:          (rw.installation || "raceway").toLowerCase(),
    depth_m:               Number(rw.depth_m || 0.6),
    length_m:              Number(load.length_m || 30),
    load_amps:             Number(load.load_amps || 200),
  };
}

function CableAmpacitySLDInner({ onChange }) {
  const [nodes, setNodes, onNodesChange] = useNodesState(INITIAL_NODES);
  const [edges, setEdges, onEdgesChange] = useEdgesState(INITIAL_EDGES);
  const [sel, setSel] = useState({ node: null, edge: null });
  const idCounter = useRef(10);

  useEffect(() => { onChange?.(graphToCableAmpacity(nodes)); }, [nodes, onChange]);

  const conductorCount = useMemo(
    () => nodes.filter((n) => n.type === "CONDUCTOR").length, [nodes]);

  const onConnect = useCallback((c) => {
    if (c.source === c.target) return;
    setEdges((es) => addEdge({ ...c, id: `e${idCounter.current++}`, ...edgeStyle() }, es));
  }, [setEdges]);

  const addConductor = () => {
    const id = `C${idCounter.current++}`;
    const idx = conductorCount;
    const phase = ["A", "B", "C", "N", "G"][idx % 5];
    setNodes((ns) => [...ns, {
      id, type: "CONDUCTOR", position: conductorPos(idx),
      data: { id, tag: `L${idx + 1}`, phase },
    }]);
  };

  const addNode = (type) => {
    if (type === "CONDUCTOR") return addConductor();
    const id = `${type[0]}${idCounter.current++}`;
    const defaults = type === "RACEWAY"
      ? { tag: id, installation: "raceway", ambient_c: 30, depth_m: 0.6 }
      : { tag: id, awg_or_kcmil: "4/0", length_m: 30, load_amps: 200 };
    setNodes((ns) => [...ns, {
      id, type,
      position: { x: 120 + Math.random() * 400, y: 60 + Math.random() * 260 },
      data: { id, ...defaults },
    }]);
  };

  const clearAll = () => { setNodes([]); setEdges([]); setSel({ node: null, edge: null }); };
  const onSelectionChange = useCallback(({ nodes: ns, edges: es }) =>
    setSel({ node: ns[0] || null, edge: es[0] || null }), []);

  const patchNode = (key, value) => {
    if (!sel.node) return;
    setNodes((all) => all.map((n) => n.id === sel.node.id
      ? { ...n, data: { ...n.data, [key]: value } } : n));
    setSel((s) => ({ ...s, node: { ...s.node, data: { ...s.node.data, [key]: value } } }));
  };
  const deleteSel = () => {
    if (sel.node) {
      const id = sel.node.id;
      setNodes((ns) => ns.filter((n) => n.id !== id));
      setEdges((es) => es.filter((e) => e.source !== id && e.target !== id));
    } else if (sel.edge) {
      const id = sel.edge.id;
      setEdges((es) => es.filter((e) => e.id !== id));
    }
    setSel({ node: null, edge: null });
  };

  return (
    <div className="h-[520px] border border-white/10 bg-[#0A0C10] relative" data-testid="amp-sld-canvas">
      <Toolbar>
        <ToolbarBtn colour="#B0A0FF" onClick={() => addNode("RACEWAY")}
          testid="amp-add-raceway" title="Add raceway">
          <Plus size={10} className="inline" /> raceway
        </ToolbarBtn>
        <ToolbarBtn colour="#00E5FF" onClick={() => addNode("CONDUCTOR")}
          testid="amp-add-conductor" title="Add conductor">
          <Plus size={10} className="inline" /> conductor
        </ToolbarBtn>
        <ToolbarBtn colour="#FF9F0A" onClick={() => addNode("LOAD")}
          testid="amp-add-load" title="Add load">
          <Plus size={10} className="inline" /> load
        </ToolbarBtn>
        <ToolbarDivider />
        <div className="px-2 py-1 text-[10px] font-mono text-gray-400" data-testid="amp-count">
          {conductorCount} conductor{conductorCount === 1 ? "" : "s"} in raceway
        </div>
        <ToolbarDivider />
        <ToolbarBtn colour="#FF3B30" onClick={deleteSel}
          testid="amp-delete-selected" title="Delete selected">
          <Trash size={10} className="inline" /> delete
        </ToolbarBtn>
        <ToolbarBtn colour="#8792A2" onClick={clearAll}
          testid="amp-clear" title="Reset">reset</ToolbarBtn>
      </Toolbar>

      <ReactFlow
        nodes={nodes} edges={edges}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onSelectionChange={onSelectionChange}
        fitView
        proOptions={{ hideAttribution: true }}
        colorMode="dark"
      >
        <Background gap={16} color="#ffffff08" />
        <Controls className="!bg-[#12151C] !border-white/10" showInteractive={false} />
      </ReactFlow>

      {sel.node && (
        <SidePanel
          title={sel.node.data.tag || sel.node.data.id}
          subtitle={sel.node.type.toLowerCase()}
          node={sel.node}
          fields={FIELDS[sel.node.type] || []}
          onChange={patchNode}
          onDelete={deleteSel}
          onClose={() => setSel({ node: null, edge: null })}
        />
      )}
    </div>
  );
}

export default function CableAmpacitySLD({ onChange }) {
  return (
    <ReactFlowProvider>
      <CableAmpacitySLDInner onChange={onChange} />
    </ReactFlowProvider>
  );
}
